import { useErrorId } from '@lokalise/louis';
import type { FormikErrors } from 'formik';
import type { Ref } from 'react';
import React, { forwardRef, useEffect, useState } from 'react';
import type { Props as DefaultProps, GroupBase, SelectInstance } from 'react-select';
import DefaultSelect from 'react-select';
import type { AsyncProps } from 'react-select/async';
import AsyncSelect from 'react-select/async';
import type { AsyncCreatableProps } from 'react-select/async-creatable';
import AsyncCreatableSelect from 'react-select/async-creatable';
import type { CreatableProps } from 'react-select/creatable';
import CreatableSelect from 'react-select/creatable';

import { Error2 } from '../components';
import ClearIndicator from './components/ClearIndicator';
import DropdownIndicator from './components/DropdownIndicator';
import MultiValueLabel from './components/MultiValueLabel';
import Option from './components/Option';
import { Container, CustomSelect, focusOutlineVisibilityClassName } from './styles';

type AsyncSelectProps<
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = Creatable extends true
    ? AsyncCreatableProps<OptionType, IsMulti, GroupType>
    : AsyncProps<OptionType, IsMulti, GroupType>;

type SyncSelectProps<
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = Creatable extends true
    ? CreatableProps<OptionType, IsMulti, GroupType>
    : DefaultProps<OptionType, IsMulti, GroupType>;

type CheckAsync<
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = Async extends true
    ? AsyncSelectProps<Creatable, OptionType, IsMulti, GroupType>
    : SyncSelectProps<Creatable, OptionType, IsMulti, GroupType>;

export type SelectFieldProps<
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = CheckAsync<Async, Creatable, OptionType, IsMulti, GroupType> & {
    // Disabling `styles` prop without flattening "parent" type
    styles?: never;
    theme?: never;
    /**
     * Renders a form validation `InputErrorMessage` when passed as `string | string[]`, or just applies error styling when `true`.
     * */
    error?: boolean | string | readonly string[] | FormikErrors<OptionType>[];
    async?: boolean;
    /**
     * Toggles whether the input accepts custom options added by the user.
     * */
    creatable?: boolean;
};

const InnerSelect = <
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
    {
        async,
        creatable,
        error,
        isDisabled,
        components,
        onChange,
        ...rest
    }: SelectFieldProps<Async, Creatable, OptionType, IsMulti, GroupType>,
    ref: Ref<SelectInstance>,
) => {
    const errorMsg = error && typeof error === 'object' && error !== null ? Object.values(error) : error;

    const errorId = useErrorId();

    const isError = Array.isArray(error) ? error.length > 0 : !!error;

    // The built-in hover/focus styling logic for react-select causes performance issues
    // that make select really slow if it contains many options (e.g. >500 options)
    // https://github.com/JedWatson/react-select/issues/3128
    //
    // Instead of these methods we use CSS selectors for hover and focus
    // along with event listeners on the parent container to switch between
    // mouse hover styling and keyboard focus styling
    const [selectContainerRef, setSelectContainerRef] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!selectContainerRef) {
            return undefined;
        }

        const keyDownHandler = (e: KeyboardEvent) => {
            if (['Tab', 'ArrowUp', 'ArrowDown'].includes(e.code)) {
                selectContainerRef.classList.add(focusOutlineVisibilityClassName);
            }
        };
        const mouseOverHandler = () => {
            selectContainerRef.classList.remove(focusOutlineVisibilityClassName);
        };

        selectContainerRef.addEventListener('mouseover', mouseOverHandler);
        selectContainerRef.addEventListener('keydown', keyDownHandler);

        return () => {
            selectContainerRef.removeEventListener('mouseover', mouseOverHandler);
            selectContainerRef.removeEventListener('keydown', keyDownHandler);
        };
    }, [selectContainerRef]);

    return (
        <Container ref={setSelectContainerRef}>
            <CustomSelect
                disabled={isDisabled}
                isDisabled={isDisabled}
                as={
                    // eslint-disable-next-line no-nested-ternary
                    async
                        ? creatable
                            ? AsyncCreatableSelect
                            : AsyncSelect
                        : creatable
                        ? CreatableSelect
                        : DefaultSelect
                }
                ref={ref}
                classNamePrefix="react-select"
                error={isError}
                aria-invalid={isError} // Seems like this is currently unsupported
                components={{ DropdownIndicator, Option, MultiValueLabel, ClearIndicator, ...components }}
                noOptionsMessage={() => 'No matches found'}
                {...(creatable ? { formatCreateLabel: (value: string) => `+ Add "${value}"` } : {})}
                onChange={onChange}
                {...rest}
            />
            {!!errorMsg && typeof errorMsg !== 'boolean' && <Error2 id={errorId}>{errorMsg}</Error2>}
        </Container>
    );
};

const Select2 = forwardRef(InnerSelect) as <
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
    props: SelectFieldProps<Async, Creatable, OptionType, IsMulti, GroupType> & {
        ref?: Ref<SelectInstance>;
    },
) => ReturnType<typeof InnerSelect>;

export default Select2;
